import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { PayOptions } from "@/components/PayOptions";

export default function PayFee() {
  const { feeId } = useParams();
  const [info, setInfo] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/fees/${feeId}/pay`).then((r) => setInfo(r.data)).catch(() => setInfo(false));
  }, [feeId]);

  const pay = async (method) => {
    setPaying(method);
    try {
      const f = info.fee;
      const { data } = await api.post("/payments/fee-checkout", {
        club_id: f.club_id, fee_id: f.id, player_id: f.player_id,
        fee_type: f.fee_type, amount: f.amount, description: f.description || "",
        origin_url: window.location.origin, payment_method: method,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setPaying(false); }
  };

  if (info === false) return (
    <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground" data-testid="pay-not-found">Payment link not found or expired.</div>
  );
  if (!info) return (
    <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
  );

  const { fee, club, player_name, paid } = info;
  const cur = club?.currency || "USD";

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="pay-page">
        <div className="flex flex-col items-center text-center mb-6">
          {club?.logo ? <img src={club.logo} alt="" className="h-14 w-14 rounded-2xl object-cover border border-border" /> : <Logo size={40} showText={false} />}
          <h1 className="font-heading font-extrabold text-2xl tracking-tight mt-4">{club?.name}</h1>
        </div>
        <div className="rounded-2xl border border-border bg-card p-7">
          {paid ? (
            <div className="text-center py-4" data-testid="pay-already-paid">
              <CheckCircle2 size={48} className="mx-auto text-secondary mb-4" />
              <p className="font-heading font-bold text-xl">This payment is settled</p>
              <p className="text-sm text-muted-foreground mt-1">Thanks — no further action needed.</p>
            </div>
          ) : (
            <>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{fee.fee_type}</p>
              {player_name && <p className="text-sm text-muted-foreground mt-0.5">For {player_name}</p>}
              <p className="font-heading font-extrabold text-4xl mt-3" data-testid="pay-amount">{cur} {fee.amount?.toFixed(2)}</p>
              {fee.due_date && <p className="text-sm text-muted-foreground mt-1">Due {fee.due_date}</p>}
              {fee.description && <p className="text-sm text-muted-foreground mt-3">{fee.description}</p>}
              <div className="mt-6">
                <PayOptions club={{ ...club, ...(info.options?.pass_fees_to_payer !== undefined ? { pass_fees_to_payer: info.options.pass_fees_to_payer, bank_debit_enabled: info.options.bank_debit_available } : {}) }} amount={fee.amount} onPay={pay} paying={paying} testId="pay-now" />
              </div>
              <p className="text-[11px] text-muted-foreground text-center mt-3">Secure payment powered by Stripe.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
